import { StandardEvents } from '@shopify/events';

/**
 * Golden Hour: the pack chooser above Add to cart.
 *
 * What it has to get right, in order of how badly it goes wrong:
 *
 * 1. The cart. Horizon posts the product form with `id` and `quantity`,
 *    which is one line. `/cart/add.js` honours `items[]` as well, and it
 *    honours both at the same time: the `id` line is added AND every
 *    `items[]` line. So the first item of a pack travels as the form's own
 *    `id`, and only items two and up are written in as hidden `items[]`
 *    inputs. They are written into the form and left there, because
 *    Horizon reads the form before any submit listener here could run.
 *
 * 2. Never a choice she did not make. Every row starts empty. In the mode
 *    where the first item is chosen in the pack (data-first-row="own", the
 *    Sianna boots), that holds for the first row too: the variant picker
 *    above starts on the first variant, and a pack whose first pair was
 *    "whatever the picker happened to say" put a 6.5 in Black in the cart
 *    for someone who never chose it. The first row, once complete, sets the
 *    picker, so the form's `id` is her choice. Only if she has already
 *    tapped the picker herself does the first row start from that.
 *
 * 3. Never offering a combination that does not exist. Every colour and
 *    size pair is checked against the variant list. A pair that is not for
 *    sale is said so in words and blocks the button, rather than failing
 *    at the cart.
 *
 * 4. Saying which row is incomplete, at that row, and keeping the button
 *    off until every row is complete and the picker agrees with row one.
 *
 * Colour per row is either a <select> or a group of swatch radios
 * (colour_style: swatches). The helpers #colour / #setColour hide which.
 *
 * After each change the block announces its state with a `ghw:bundle`
 * event (count, lines in words, ready), for the second Add to cart lower
 * on the page (ghw-buy-again).
 */
class GhwBundleComponent extends HTMLElement {
  #variants = [];
  #abort = new AbortController();
  /** She has tapped the variant picker herself on this page. */
  #touched = false;
  /** A picker update of our own is under way; do not echo it back. */
  #syncing = false;
  #pushing = false;
  #pushAgain = false;

  connectedCallback() {
    const json = this.querySelector('[data-ghw-bundle-variants]');
    try {
      this.#variants = JSON.parse(json?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    if (!this.#variants.length) return;

    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    // A safety net only: Horizon reads the form before this can run, which
    // is why the lines are written in advance (see 1 above).
    document.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener('change', this.#onPickerChange, { capture: true, signal });
    document.addEventListener('ghw:size-pending', () => { this.#syncFirstRow(); this.#update(); }, { signal });
    document.addEventListener('ghw:stock-ready', () => this.#update(), { signal });
    // The guard: before Horizon's form or the sticky bar sees a tap on a
    // held button, take her to the first row that still needs something.
    window.addEventListener('click', this.#onHeldClick, { capture: true, signal });

    this.#syncFirstRow();
    this.#applyTier();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get #ownFirst() {
    return this.dataset.firstRow === 'own';
  }

  /** @returns {HTMLFormElement|null} */
  get #form() {
    return document.querySelector('product-form-component form[action*="/cart/add"]:not(.quick-add-modal form)')
      ?? document.querySelector('form[action*="/cart/add"]:not(.quick-add-modal form):not(.ghw-again__form)');
  }

  /** @returns {HTMLInputElement|null} */
  get #idInput() {
    return this.#form?.querySelector('input[name="id"]') ?? null;
  }

  get #pickerVariant() {
    const id = Number(this.#idInput?.value || 0);
    return this.#variants.find((v) => v.id === id) ?? null;
  }

  /** The option block the shopper has ticked. */
  get #option() {
    const checked = this.querySelector('.ghw-bundle__radio:checked');
    return checked?.closest('.ghw-bundle__option') ?? null;
  }

  get #count() {
    return Number(this.#option?.dataset.count || 1);
  }

  #find(colour, size) {
    return this.#variants.find((v) => v.colour === colour && v.size === size) ?? null;
  }

  /** Rows of the chosen pack that she fills in herself. */
  #editableRows(option = this.#option) {
    if (!option) return [];
    return [...option.querySelectorAll('.ghw-bundle__row')].filter((row) => row.querySelector('[data-kind="size"]'));
  }

  #colour(row) {
    const select = row.querySelector('select[data-kind="colour"]');
    if (select) return select.value;
    return row.querySelector('input[data-kind="colour"]:checked')?.value ?? '';
  }

  #setColour(row, value) {
    const select = row.querySelector('select[data-kind="colour"]');
    if (select) {
      select.value = value;
      return;
    }
    for (const input of row.querySelectorAll('input[data-kind="colour"]')) input.checked = input.value === value;
  }

  #colourField(row) {
    return row.querySelector('select[data-kind="colour"]') ?? row.querySelector('[data-kind="colour-group"]');
  }

  #size(row) {
    return row.querySelector('[data-kind="size"]')?.value ?? '';
  }

  /** The first row of the chosen pack, in the mode where she fills it in. */
  get #firstOwnRow() {
    if (!this.#ownFirst) return null;
    return this.#option?.querySelector('.ghw-bundle__row[data-row="1"]') ?? null;
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.classList.contains('ghw-bundle__radio')) {
      this.#applyTier();
      return;
    }
    const row = target.closest('.ghw-bundle__row');
    if (row && target.matches('[data-kind="colour"], [data-kind="size"]')) {
      this.#refreshRow(row);
      this.#update();
      if (row === this.#firstOwnRow) this.#pushFirstRow();
    }
  };

  /** A tap on the picker itself, by her, not by #pushFirstRow. */
  #onPickerChange = (event) => {
    if (!event.isTrusted || this.#syncing) return;
    if (event.target instanceof Element && event.target.closest('.variant-picker')) this.#touched = true;
  };

  /** Show the panel of the chosen pack, hide the rest, then check it over. */
  #applyTier() {
    for (const panel of this.querySelectorAll('.ghw-bundle__panel')) {
      const option = panel.closest('.ghw-bundle__option');
      const chosen = option?.querySelector('.ghw-bundle__radio')?.checked === true;
      panel.hidden = !chosen;
      for (const field of panel.querySelectorAll('select, input')) field.disabled = !chosen;
    }
    this.#syncFirstRow();
    this.#fillFirstFromPicker();
    for (const row of this.querySelectorAll('.ghw-bundle__row')) this.#refreshRow(row);
    this.#update();
  }

  /** Validate, write the lines, tell the rest of the page. */
  #update() {
    this.#validate();
    this.#writeLines();
    this.#announce();
  }

  /** Picker mode: read the picker's current choice into the first row. */
  #syncFirstRow = () => {
    const variant = this.#pickerVariant;
    for (const row of this.querySelectorAll('.ghw-bundle__row:has([data-first-value])')) {
      const value = row.querySelector('[data-first-value]');
      if (value) value.textContent = variant ? `${variant.colour}, ${variant.size}` : this.dataset.chooseAbove || 'Choose a size above';
      this.#paint(row.querySelector('.ghw-bundle__swatch'), variant?.colour);
    }
  };

  /**
   * Own mode: the first row takes the picker's choice only when that is a
   * choice she made, and only into an empty row.
   */
  #fillFirstFromPicker() {
    const row = this.#firstOwnRow;
    const variant = this.#pickerVariant;
    if (!row || !variant || !this.#touched) return;
    if (this.#colour(row) || this.#size(row)) return;
    this.#setColour(row, variant.colour);
    const size = row.querySelector('[data-kind="size"]');
    if (size) size.value = variant.size;
    this.#refreshRow(row);
  }

  /**
   * Own mode: set the picker above to the first row, one option at a time.
   *
   * One round at a time. A colour and then a size tapped in quick
   * succession used to start two rounds side by side; the first one
   * finishing switched #syncing off while the second was still under way,
   * and the picker update in between was taken for her own change and
   * written back into row one: Green 8 became Green 8.5, and that is what
   * would have gone into the cart. Now a change during a round only asks
   * for one more round afterwards, and #syncing stays on until the last.
   */
  async #pushFirstRow() {
    if (this.#pushing) {
      this.#pushAgain = true;
      return;
    }
    this.#pushing = true;
    this.#syncing = true;
    try {
      do {
        this.#pushAgain = false;
        const row = this.#firstOwnRow;
        if (!row) break;
        const variant = this.#find(this.#colour(row), this.#size(row));
        if (!variant?.available || this.#pickerVariant?.id === variant.id) continue;

        for (const [position, value] of [
          [Number(this.dataset.colourPosition), variant.colour],
          [Number(this.dataset.sizePosition), variant.size],
        ]) {
          const fieldset = document.querySelectorAll('.variant-picker fieldset')[position - 1];
          const input = fieldset && [...fieldset.querySelectorAll('input[type="radio"]')].find((i) => i.value === value);
          if (!input) continue;
          // While no size is chosen yet (ghw-size-pending.js) a size can look
          // ticked for a moment, because the server ticks one after every
          // colour change until that script takes it out again. Skipping it
          // would leave the picker without her size, so it is unticked first
          // and then tapped, which is the change that ends the waiting.
          if (input.checked && document.documentElement.hasAttribute('data-ghw-size-pending')) input.checked = false;
          if (input.checked) continue;
          const landed = this.#nextSelect();
          input.click();
          await landed;
        }
      } while (this.#pushAgain);
    } finally {
      this.#pushing = false;
      this.#syncing = false;
      this.#update();
    }
  }

  /** Resolves when Horizon has finished the next variant change, or after 6s. */
  #nextSelect() {
    return new Promise((resolve) => {
      const timer = window.setTimeout(resolve, 6000);
      document.addEventListener(
        StandardEvents.productSelect,
        (event) => {
          Promise.resolve(event.promise)
            .catch(() => {})
            .then(() => {
              window.clearTimeout(timer);
              resolve();
            });
        },
        { once: true }
      );
    });
  }

  #onProductSelect = async (event) => {
    try {
      await event.promise;
    } catch {
      /* the picker aborted; the next event will land */
    }
    this.#syncFirstRow();

    // Own mode: she changed the picker herself after filling in the first
    // row. The picker is what goes into the cart, so the row follows it
    // rather than the two quietly disagreeing.
    const row = this.#firstOwnRow;
    const variant = this.#pickerVariant;
    if (row && variant && !this.#syncing) {
      const current = this.#find(this.#colour(row), this.#size(row));
      if (current && current.id !== variant.id) {
        this.#setColour(row, variant.colour);
        const size = row.querySelector('[data-kind="size"]');
        if (size) size.value = variant.size;
        this.#refreshRow(row);
      } else if (!current) {
        this.#fillFirstFromPicker();
      }
    }

    // The morph that follows a variant change rebuilds the form and drops
    // anything that was not in the new HTML, so the lines are written again.
    this.#update();
  };

  /**
   * Take the colour from the swatches in the picker above, so the circle in
   * the pack is the same circle she just tapped. No second list of colours
   * to keep in step with the shop.
   */
  #paint(swatch, colour) {
    if (!swatch) return;
    if (!colour) {
      swatch.style.removeProperty('--swatch-background');
      return;
    }
    const input = Array.from(document.querySelectorAll('.variant-option input[type="radio"]')).find(
      (i) => i.value === colour
    );
    const source = input?.closest('label')?.querySelector('.swatch');
    const painted = source && getComputedStyle(source).getPropertyValue('--swatch-background').trim();
    if (painted) swatch.style.setProperty('--swatch-background', painted);
  }

  /** Grey out the sizes that this colour does not come in, and repaint. */
  #refreshRow(row) {
    if (!row) return;
    const sizeSelect = row.querySelector('[data-kind="size"]');
    if (!sizeSelect) return;

    const colour = this.#colour(row);
    for (const option of sizeSelect.options) {
      if (option.value === '') continue;
      // No colour yet: every size stays offered, there is nothing to rule out.
      const variant = colour ? this.#find(colour, option.value) : null;
      option.disabled = colour ? !variant?.available : false;
    }
    // Only correct a size she actually picked. Clearing it back to empty
    // would undo her choice; leaving a sold-out one selected would lie.
    if (sizeSelect.value !== '' && sizeSelect.selectedOptions[0]?.disabled) {
      sizeSelect.value = '';
    }
    this.#paint(row.querySelector('.ghw-bundle__swatch'), colour || null);
    const name = row.querySelector('[data-colour-name]');
    if (name) name.textContent = colour;
  }

  /** Say at the row itself what is still missing. */
  #markRows() {
    for (const row of this.#editableRows()) {
      const colourField = this.#colourField(row);
      const sizeSelect = row.querySelector('[data-kind="size"]');
      const note = row.nextElementSibling?.matches?.('[data-missing]') ? row.nextElementSibling : null;
      if (!colourField || !sizeSelect) continue;

      const noColour = this.#colour(row) === '';
      const noSize = sizeSelect.value === '';
      colourField.toggleAttribute('data-empty', noColour);
      sizeSelect.toggleAttribute('data-empty', noSize);

      if (!note) continue;
      let text = '';
      if (noColour && noSize) text = this.dataset.missingBoth || '';
      else if (noColour) text = this.dataset.missingColour || '';
      else if (noSize) text = this.dataset.missingSize || '';
      note.textContent = text;
      note.hidden = text === '';
      const id = note.id || `${sizeSelect.id}-missing`;
      note.id = id;
      for (const field of [colourField, sizeSelect]) {
        if (text) field.setAttribute('aria-describedby', id);
        else field.removeAttribute('aria-describedby');
      }
    }
  }

  /**
   * Stock per row, from the low stock line (blocks/ghw-low-stock), the one
   * place that holds Shopify's real numbers. Two things:
   *
   * - under a row whose variant is low, the same line as above Add to cart;
   * - when the pack asks for more of one variant than there are, and Shopify
   *   will not sell past zero, the rows over the limit say so and the pack
   *   cannot be added. Otherwise the cart would take fewer than she chose,
   *   or refuse, and she would find out at the cart.
   *
   * @returns {boolean} true when the pack asks for more than there is
   */
  #markStock() {
    const stock = document.querySelector('ghw-low-stock');
    const rows = this.#editableRows();
    const lines = rows.map((row) => this.#find(this.#colour(row), this.#size(row)));

    // Picker mode: the first item is the picker's, and it counts too.
    const demand = new Map();
    if (!this.#ownFirst && this.#count > 1 && this.#pickerVariant) {
      demand.set(this.#pickerVariant.id, 1);
    }

    let over = false;
    rows.forEach((row, index) => {
      const variant = lines[index];
      const note = this.#stockNote(row);
      let text = '';
      if (variant && stock && typeof stock.stockFor === 'function') {
        const wanted = (demand.get(variant.id) || 0) + 1;
        demand.set(variant.id, wanted);
        const record = stock.stockFor(variant.id);
        if (record && record.deny && record.q != null && wanted > record.q) {
          over = true;
          text = (this.dataset.overText || '')
            .replace('[count]', String(record.q))
            .replace('[variant]', record.label || '')
            .replace('[item]', this.dataset.itemWord || '');
        } else {
          text = stock.textFor(variant.id);
        }
      }
      if (note) {
        const span = note.querySelector('[data-stock-text]');
        if (span && span.textContent !== text) span.textContent = text;
        note.hidden = text === '';
      }
    });
    return over;
  }

  /** The stock line that belongs to a row: the second paragraph after it. */
  #stockNote(row) {
    let el = row.nextElementSibling;
    while (el && !el.matches('.ghw-bundle__row')) {
      if (el.matches('[data-stock]')) return el;
      el = el.nextElementSibling;
    }
    return null;
  }

  /**
   * The variant for every row she fills in, in order; null where a row is
   * incomplete or not for sale.
   */
  #chosen() {
    if (this.#count < 2) return [];
    return this.#editableRows().map((row) => {
      const variant = this.#find(this.#colour(row), this.#size(row));
      return variant?.available ? variant : null;
    });
  }

  /**
   * The lines that go in as `items[]`: every chosen row except the one that
   * travels as the form's `id`. In picker mode that one is not a row here
   * at all; in own mode it is row one.
   */
  #rows() {
    const chosen = this.#chosen();
    return this.#ownFirst ? chosen.slice(1) : chosen;
  }

  /** Every item in the pack has to be a real, available variant. */
  #validate() {
    const option = this.#option;
    const warning = option?.querySelector('[data-warning]');
    const chosen = this.#chosen();
    let missing = chosen.some((variant) => !variant);

    // Picker mode: the first item is whatever the picker says, and while no
    // size has been chosen there (ghw-size-pending) it says nothing yet.
    if (!this.#ownFirst && this.#count > 1 && !this.#pickerVariant) missing = true;

    // Own mode: row one only counts once the picker says the same, because
    // the picker's `id` is what the cart gets for it.
    if (!missing && this.#ownFirst && chosen.length) {
      missing = this.#syncing || this.#pickerVariant?.id !== chosen[0]?.id;
    }

    this.#markRows();
    if (this.#markStock()) missing = true;

    // Two different things can be wrong, and they need different words.
    // Nothing chosen yet is said at the row; a combination that is not
    // for sale is said once, here, because it is about the pack.
    const empty = this.#editableRows().some((row) => this.#colour(row) === '' || this.#size(row) === '');
    const unavailable = chosen.some((variant) => !variant) && !empty;

    if (warning) {
      warning.hidden = !unavailable;
      warning.textContent = unavailable ? this.dataset.unavailableText || 'Not available' : '';
    }

    // Held, not disabled: a disabled button takes no tap, and a tap that
    // does nothing reads as a broken shop. aria-disabled keeps it tappable;
    // the guard in connectedCallback turns the tap into "this row is still
    // open". Horizon may disable or enable the button on a variant change
    // for its own reasons (sold out); that stays Horizon's.
    const buttons = document.querySelectorAll(
      'product-form-component button[name="add"], sticky-add-to-cart [ref="addToCartButton"]'
    );
    for (const button of buttons) {
      if (missing) {
        button.setAttribute('aria-disabled', 'true');
      } else if (button.hasAttribute('data-ghw-bundle-held') && !document.documentElement.hasAttribute('data-ghw-size-pending')) {
        button.removeAttribute('aria-disabled');
      }
      button.toggleAttribute('data-ghw-bundle-held', missing);
    }

    this.dataset.ready = String(!missing);
    return !missing;
  }

  /**
   * Put items two and up into the product form as hidden `items[]` inputs
   * and leave them there. Horizon reads the form with new FormData(form)
   * when it submits, so what is in the form is what the cart gets.
   */
  #writeLines() {
    const form = this.#form;
    if (!form) return;

    for (const stale of form.querySelectorAll('[data-ghw-bundle-line]')) stale.remove();
    if (this.#count < 2) return;

    this.#rows().forEach((variant, index) => {
      if (!variant) return;
      for (const [name, value] of [
        [`items[${index}][id]`, String(variant.id)],
        [`items[${index}][quantity]`, '1'],
      ]) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-ghw-bundle-line', '');
        form.appendChild(input);
      }
    });
  }

  /** For the second Add to cart: what is in the pack, in words. */
  #announce() {
    const count = this.#count;
    const lines =
      count < 2
        ? []
        : this.#editableRows().map((row) => {
            const colour = this.#colour(row);
            const size = this.#size(row);
            return colour && size ? `${colour}, ${size}` : '';
          });
    if (count > 1 && !this.#ownFirst) {
      const v = this.#pickerVariant;
      lines.unshift(v ? `${v.colour}, ${v.size}` : '');
    }
    const price = this.#option?.querySelector('.ghw-bundle__price')?.textContent.trim() ?? '';
    document.dispatchEvent(
      new CustomEvent('ghw:bundle', {
        detail: { count, lines, price, ready: this.dataset.ready !== 'false' },
      })
    );
  }

  #onHeldClick = (event) => {
    if (this.#count < 2 || this.dataset.ready !== 'false') return;
    if (!(event.target instanceof Element)) return;
    const hit = event.target.closest(
      'product-form-component button[name="add"], sticky-add-to-cart [ref="addToCartButton"], .ghw-again__button'
    );
    if (!hit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showWhatIsMissing();
  };

  /**
   * Scroll to the first row with something open, light it up, focus its
   * first empty field, and let its message be read out again.
   */
  #showWhatIsMissing() {
    this.#validate();
    const option = this.#option;
    if (!option) return;
    const message =
      option.querySelector('[data-missing]:not([hidden])') ??
      option.querySelector('[data-stock]:not([hidden])') ??
      option.querySelector('[data-warning]:not([hidden])');
    let row = message?.previousElementSibling;
    while (row && !row.matches('.ghw-bundle__row')) row = row.previousElementSibling;
    const target = row ?? message ?? option;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    if (row) {
      row.setAttribute('data-ghw-row-called', '');
      window.setTimeout(() => row.removeAttribute('data-ghw-row-called'), 2500);
      const empty =
        (this.#colour(row) === '' && row.querySelector('[data-kind="colour"]')) ||
        (this.#size(row) === '' && row.querySelector('[data-kind="size"]')) ||
        row.querySelector('[data-kind="size"]');
      empty?.focus({ preventScroll: true });
    }
    // A status region only speaks when its text changes; only the words
    // are reset, not the dot in front of a stock line.
    const words = message?.querySelector('[data-stock-text]') ?? message;
    if (words) {
      const text = words.textContent;
      words.textContent = '';
      window.setTimeout(() => (words.textContent = text), 60);
    }
  }

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
    if (form.closest('quick-add-dialog, .quick-add-modal, ghw-buy-again')) return;
    if (this.#count < 2) return;

    if (!this.#validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const firstMissing = this.querySelector('[data-missing]:not([hidden]), [data-warning]:not([hidden])');
      firstMissing?.scrollIntoView({ block: 'center' });
    }
  };
}

if (!customElements.get('ghw-bundle-component')) {
  customElements.define('ghw-bundle-component', GhwBundleComponent);
}
